// src/components/desk/CadastralCard.jsx — what the Public Registry says about this flat's parcel.
// ============================================================================
// Owner, 2026-09-20: *"whenever it's attached to a property on Desk we can have a cadastral
// section where we show all public information about that property."*
//
// The source is the registry's own map portal, resolved once per PARCEL and stored (core 1555,
// lib/core_cadastral.js). This card only READS what we hold: an empty answer means "not resolved
// yet", never "no such parcel", and the card renders nothing rather than inventing a placeholder.
//
// ── THE THREE THINGS THIS CARD EXISTS TO SAY ────────────────────────────────
// 1. THE OFFICIAL ADDRESS. For most of this stock it has no street — measured on the 1,057 bog.ge
//    properties resolved on 2026-09-20, only 19% of official addresses name one. A village or a
//    block IS the address there; that is not missing data, and the card must not apologise for it.
// 2. WHICH CODE WAS ACTUALLY ASKED. The registry indexes parcels, not flats, so a unit code
//    (≥6 segments) is truncated to its first five. The card says so out loud, because a user
//    comparing our answer against their own lookup must see the same string we used.
// 3. WHETHER OUR PIN IS INSIDE THE PARCEL. Not a distance with a threshold — containment. It is
//    the one thing here an agent can act on, and on that first batch 637 of 684 pinned properties
//    fell OUTSIDE their own parcel, median 2.9 km out. The pin is ours; the parcel is the state's.
//
// ── WHAT THIS CARD MUST NEVER SHOW ──────────────────────────────────────────
// The registry also publishes the owners' names and their personal numbers. Owner ruling
// 2026-09-20: we may store that internally and never publish it — and that is a separate change
// shipping with its own processing-register entry and a role revocation. The endpoint does not
// return it today. Do not add it here "because it is public at the source".

import { useEffect, useState } from 'react'
import { t } from '../../lib/i18n.js'
import { getPropertyParcels } from '../../db/core.js'
import { card, ROW, ROW_LEFT, ROW_VAL, SECTION_LABEL, Badge } from './primitives.jsx'

const REGISTRY_URL = 'https://maps.gov.ge/map/portal'

// inside → our pin agrees with the state. outside → a correction to make, and the distance says
// how big. no_pin → we never had one, which for bog.ge listings is the normal case.
function VerdictBadge({ verdict, distance }) {
    if (!verdict) return null
    if (verdict === 'inside') return <Badge variant="green" label={t('cad_pin_inside')} />
    if (verdict === 'outside') {
        const d = Number(distance)
        const label = Number.isFinite(d)
            ? (d >= 1000 ? `${(d / 1000).toFixed(d >= 10000 ? 0 : 1)} km` : `${Math.round(d)} m`)
            : null
        return <Badge variant="amber" label={label ? `${t('cad_pin_outside')} · ${label}` : t('cad_pin_outside')} />
    }
    if (verdict === 'no_pin') return <Badge variant="default" label={t('cad_pin_none')} />
    return null
}

export default function CadastralCard({ propertyId }) {
    const [rows, setRows] = useState(null)
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        if (!propertyId) return
        const ac = new AbortController()
        let alive = true
        getPropertyParcels(propertyId, { signal: ac.signal })
            .then(r => { if (alive) setRows(r) })
            .catch(e => { if (alive && e?.name !== 'AbortError') { setFailed(true); console.error('[cadastral]', e.message) } })
        return () => { alive = false; ac.abort() }
    }, [propertyId])

    // Nothing resolved and nothing broken: render nothing. A card that says "no data" on every
    // property without a code is noise on most of the corpus.
    if (failed || !rows || rows.length === 0) return null

    return (
        <div className={card}>
            <div className={SECTION_LABEL}>{t('cad_section')}</div>
            {rows.map(r => {
                const p = r.parcel || {}
                const areaGap = Number(p.registry_area_sqm) && Number(p.polygon_area_sqm)
                    && Math.abs(p.registry_area_sqm - p.polygon_area_sqm) / p.registry_area_sqm > 0.05
                return (
                    <div key={r.asked_code} className="mt-2 first:mt-0">
                        <div className={ROW}>
                            <span className={ROW_LEFT}>{t('cad_code')}</span>
                            <span className={`${ROW_VAL} tabular-nums`}>
                                {r.cadastral_code || r.asked_code}
                                {/* The user's own lookup must be reproducible: name the string we asked. */}
                                {r.truncated && (
                                    <span className="text-muted-foreground"> · {t('cad_truncated_from')} {r.asked_code}</span>
                                )}
                            </span>
                        </div>

                        {p.official_address && (
                            <div className={ROW}>
                                <span className={ROW_LEFT}>{t('cad_official_address')}</span>
                                <span className={ROW_VAL}>{p.official_address}</span>
                            </div>
                        )}

                        {Number(p.registry_area_sqm) > 0 && (
                            <div className={ROW}>
                                <span className={ROW_LEFT}>{t('cad_parcel_area')}</span>
                                <span className={`${ROW_VAL} tabular-nums`}>
                                    {Math.round(p.registry_area_sqm).toLocaleString()} {t('cad_sqm')}
                                    {/* Our own polygon area is a free cross-check on the geometry, not a second fact. */}
                                    {areaGap && <span className="text-muted-foreground"> · {t('cad_area_mismatch')}</span>}
                                </span>
                            </div>
                        )}

                        {p.parcel_type && (
                            <div className={ROW}>
                                <span className={ROW_LEFT}>{t('cad_parcel_type')}</span>
                                <span className={ROW_VAL}>{p.parcel_type}</span>
                            </div>
                        )}

                        {/* Ownership TYPE, never the owners. თანასაკუთრება means a multi-flat building,
                            which is also why a parcel-level code can never identify one flat. */}
                        {p.ownership_type && (
                            <div className={ROW}>
                                <span className={ROW_LEFT}>{t('cad_ownership_type')}</span>
                                <span className={ROW_VAL}>{p.ownership_type}</span>
                            </div>
                        )}

                        {p.previous_code && (
                            <div className={ROW}>
                                <span className={ROW_LEFT}>{t('cad_previous_code')}</span>
                                <span className={`${ROW_VAL} tabular-nums`}>{p.previous_code}</span>
                            </div>
                        )}

                        <div className={ROW}>
                            <span className={ROW_LEFT}>{t('cad_our_pin')}</span>
                            <span className={ROW_VAL}>
                                <VerdictBadge verdict={r.pin_verdict} distance={r.pin_distance_m} />
                            </span>
                        </div>

                        <div className="mt-1 text-xs text-muted-foreground">
                            <a href={REGISTRY_URL} target="_blank" rel="noreferrer" className="underline">
                                {t('cad_open_registry')}
                            </a>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
